import * as React from 'react';
import { action, computed, observable } from 'mobx';
import { ChangeEvent } from 'react';
import { observer } from 'mobx-react';

import { Permissions, ApiUrls, Sizes } from '@app/AppConstants';
import { PersonImage, UploadImageResult } from '@app/Models';

import { CompletionType, PromiseCompletion } from '@app/Classes/PromiseCompletion';
import ApiService from '@app/Services/ApiService';
import { securityService } from '@app/Services/SecurityService';

import { Loading } from '@app/Components/Loading';
import { modalService } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

type ChangePersonImageCallback = (personId: string) => void;

type PersonImageBlockProps = {
    personId: string;
    hasPersonImage: boolean;
    originalImageUrl?: string;
    personImages?: Array<PersonImage>;
    onChangePersonImageHandler?: ChangePersonImageCallback;
};

@observer
export class PersonImageBlock extends React.Component<PersonImageBlockProps, {}> {
    private _store: PersonImageStore;

    constructor(props: PersonImageBlockProps) {
        super(props);
        this._store = new PersonImageStore(this.props);
    }

    componentDidUpdate(prevProps: Readonly<PersonImageBlockProps>): void {
        if (prevProps.personId !== this.props.personId) {
            this._store.onReceiveParams(this.props);
        }
    }

    public render() {
        const { changeHandler, originUrl, isUploaded, hasError } = this._store;
        const {hasPermission, permissionCss } = securityService;

        const canEdit = hasPermission(Permissions.EditPersonPicture, !AzureAuth.isReadOnly);
        const canEditCss = permissionCss(Permissions.EditPersonPicture, Permissions.EditPersonPicture);
        return (
            <div className="person-image">
                <Loading loading={isUploaded.isPending} />
                <a href={originUrl} target="_blank" rel="noreferrer" className={'person-image-prev ms-Icon ms-Icon--Contact ' + (originUrl ? 'has-prev' : '') + (hasError ? ' has-error' : '')} onClick={event => { !originUrl && event.preventDefault(); }}>
                    {originUrl &&
                        <>
                            <img
                                className={'person-image-prev ms-Icon ms-Icon--Contact ' + (originUrl ? 'has-prev' : '')}
                                src={originUrl}
                                onError={this._store.errorHandler}
                            />
                            {canEdit &&
                                <div className={'person-image-delete-button ' + canEditCss} onClick={(e) => {
                                    e.preventDefault();
                                    canEdit && this.onDeleteClick(e);
                                }}>
                                    <i className="ms-Icon ms-Icon--Cancel" />
                                </div>
                            }
                        </>
                    }
                    {hasError &&
                        <i className="person-image-prev-error ms-Icon ms-Icon--Error" title="Image loading error" />
                    }
                </a>
                {!this._store.isFileFormatAccepted && <span className="ms-TextField-errorMessage errorMessage text-center">Sorry, only JPG, JPEG, PNG &amp; BMP files are allowed</span>}
                {!this._store.isFileSizeAccepted && <span className="ms-TextField-errorMessage errorMessage text-center">Sorry, max size typeof file is 5mb</span>}
                {canEdit && <div className={'person-image-field ' + canEditCss}>
                    <label className="person-image-button">
                        Upload
                        <input className="person-image-input" disabled={isUploaded.isPending || !canEdit} accept={this._store.acceptedFormats.join(',')} onChange={changeHandler} type="file" />
                    </label>
                </div>}
            </div>
        );
    }

    private onDeleteClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();
        this._store.deleteHandler();
    };
}

enum ImageStatus {
    None,
    Error
}

class PersonImageStore {
    @observable originUrl: string = '';
    @observable isUploaded: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
    acceptedFormats: string[] = ['image/jpeg', 'image/png', 'image/bmp'];
    @observable selectedFile: File | null = null;
    @observable status: ImageStatus = ImageStatus.None;

    private onChangeImage?: ChangePersonImageCallback;
    private personId: string;
    private originalImageUrl?: string;
    private personImages?: Array<PersonImage>;

    constructor(params: PersonImageBlockProps) {
        this.onReceiveParams(params);
    }

    @action
    updatePrev() {
        if (this.personImages && this.personImages.length > 0) {
            const patImage = this.personImages.find(x => x.name === 'PAT');
            if (patImage && patImage.url) {
                this.originUrl = patImage.url;
            } else {
                if (this.personImages[0].url) this.originUrl = this.personImages[0].url;
            }
        } else {
            if (this.originalImageUrl && this.originalImageUrl.length > 0) {
                this.originUrl = this.originalImageUrl;
            } else {
                this.originUrl = '';
            }
        }
    }

    @action.bound
    async changeHandler(event: ChangeEvent<HTMLInputElement>) {
        this.selectedFile = event.target.files && event.target.files[0] || null;
        if (this.selectedFile && this.isFileFormatAccepted && this.isFileSizeAccepted) {
            const formData = new FormData();
            formData.append('formFile', this.selectedFile);
            formData.append('id', this.personId || '');
            const { data } = await ApiService.postTypedData<UploadImageResult>(ApiUrls.PersonUrl + '/' + this.personId + '/image', formData, { completion: this.isUploaded });
            this.originUrl = '';
            this.originUrl = data.temporaryUrl || '';
            this._changeImageHandler();
        }
    }

    @action.bound
    async deleteHandler() {
        if (!await modalService.showConfirmation('Are you sure you want to delete person image and all its derivations?', 'Warning', { width: 400 })) {
            return;
        }

        await ApiService.deleteData(ApiUrls.PersonUrl + '/' + this.personId + '/image', { completion: this.isUploaded });
        this.originUrl = '';
        this._changeImageHandler();
    }

    @action.bound
    errorHandler() {
        this.status = ImageStatus.Error;
    }

    @action.bound
    private _changeImageHandler() {
        this.status = ImageStatus.None;

        if (this.onChangeImage) {
            this.onChangeImage(this.personId);
        }
    }

    @computed
    get isFileFormatAccepted() {
        return this.selectedFile ? this.acceptedFormats.indexOf(this.selectedFile.type) !== -1 : true;
    }

    @computed
    get isFileSizeAccepted() {
        return this.selectedFile ? this.selectedFile.size <= Sizes.maxPersonImageSize : true;
    }

    get getPersonId() {
        return this.personId;
    }

    @computed
    get hasError() {
        return this.status === ImageStatus.Error;
    }

    onReceiveParams(params: PersonImageBlockProps) {
        this.onChangeImage = params.onChangePersonImageHandler;
        this.personId = params.personId;
        this.personImages = params.personImages;
        this.originalImageUrl = params.originalImageUrl;
        this.status = ImageStatus.None;
        this.updatePrev();
    }
}